import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { qrDestinationPath, type ResolvedWaveQr } from "@/lib/campaignQr";
import { trackEvent } from "@/lib/analytics";
import { setQrAttribution } from "@/lib/firstPartyAnalytics";
import Wordmark from "@/components/Wordmark";
import waveLogo from "@/assets/jersey-shore-wave-logo.png";

const WaveQrRedirect = ({ kind }: { kind: "campaign" | "roster" | "athlete" }) => {
  const { code: pathCode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    let active = true;
    const queryCode = new URLSearchParams(location.search).get("c");
    const code = kind === "athlete" ? pathCode : queryCode;
    if (!code) {
      navigate(kind === "roster" ? "/wave#roster" : "/wave", { replace: true });
      return;
    }
    supabase.rpc("resolve_wave_qr_code", { p_code: code }).then(({ data, error }) => {
      if (!active) return;
      const resolved = (data?.[0] as ResolvedWaveQr | undefined) ?? null;
      if (error || !resolved || resolved.destination_type !== kind) {
        setInvalid(true);
        return;
      }
      const target = qrDestinationPath(resolved);
      if (!target) {
        setInvalid(true);
        return;
      }
      trackEvent("qr_scan", {
        code_id: resolved.code_id,
        placement: resolved.placement,
        campaign: resolved.campaign,
        ...(resolved.athlete_id ? { athlete_id: resolved.athlete_id } : {}),
      });
      setQrAttribution(resolved);
      navigate(target, { replace: true });
    });
    return () => { active = false; };
  }, [kind, location.search, navigate, pathCode]);

  if (!invalid) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><p role="status" className="text-muted-foreground">Opening campaign…</p></div>;
  }

  return (
    <main className="min-h-screen bg-background flex items-center justify-center p-6">
      <section className="max-w-md text-center">
        <div className="flex flex-wrap items-center justify-center gap-3"><Wordmark size="sm" /><span aria-hidden>×</span><img src={waveLogo} alt="Jersey Shore Wave" className="h-10 w-auto" /></div>
        <h1 className="mt-8 text-3xl">This QR code is not active</h1>
        <p className="mt-3 text-muted-foreground">The campaign is still available.</p>
        <Link to="/wave" className="mt-6 inline-flex min-h-11 items-center rounded-md bg-primary px-5 font-semibold text-primary-foreground">Visit the Wave campaign</Link>
      </section>
    </main>
  );
};

export default WaveQrRedirect;
